import React, { useState, useEffect, useContext, useRef } from "react";
import { Bell, X, Check, Briefcase, DollarSign, CheckCircle, AlertCircle, RefreshCw, Send, CreditCard, MapPin, Star, UserCheck, XCircle, Trash2, Smartphone, Clock, Megaphone, Zap } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { serverErrorActive } from "../config/axiosInterceptors";
import { dedupedGet } from "../utils/dedupedRequest";
import { toast } from '../utils/toast';
import { showBrowserNotification } from '../utils/showNotification';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API = `${BACKEND_URL}/api`;

// Construct WebSocket URL properly (wss for https, ws for http)
const getWebSocketURL = () => {
  const url = BACKEND_URL.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
  return url.replace(/\/$/, ''); // Remove trailing slash
};
const WS_URL = getWebSocketURL();

// Notification sound - shared reliable utility
import { playNotificationSound } from '../utils/sounds';
import { BACKEND_URL } from '../config/api';
import { getNotificationRoute } from '../utils/notificationRouting';
import './NotificationCenter.css';

const NotificationCenter = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true); // Only true on initial load
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const wsConnectedRef = useRef(false);
  const browserNotifEnabled = typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted';
  const [activeAnnouncements, setActiveAnnouncements] = useState([]);
  
  // Push notification hook
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    loading: pushLoading,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush
  } = usePushNotifications();
  
  // Handle push notification toggle
  const handlePushToggle = async () => {
    if (pushSubscribed) {
      const success = await unsubscribeFromPush();
      if (success) {
        toast.success('Push notifications disabled');
      } else {
        toast.error('Failed to disable push notifications');
      }
    } else {
      const success = await subscribeToPush();
      if (success) {
        toast.success('Push notifications enabled! You\'ll receive notifications even when the browser is closed.');
      } else {
        toast.error('Failed to enable push notifications. Please check your browser settings.');
      }
    }
  };

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && 
          panelRef.current && 
          !panelRef.current.contains(event.target) &&
          bellRef.current &&
          !bellRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Refetch notifications every time panel opens
  useEffect(() => {
    if (isOpen && user) {
      fetchNotifications(notifications.length === 0);
      fetchAnnouncements();
    }
  }, [isOpen]);

  useEffect(() => {
    if (user) {
      fetchNotifications(true); // initial load with loading state
      fetchUnreadCount().catch(() => {}); // swallow initial error
      fetchAnnouncements();
      connectWebSocket();
      
      // Fallback poll with error backoff — base 100s
      let pollCount = 0;
      let consecutiveErrors = 0;
      const BASE_POLL_INTERVAL = 100000;
      const interval = setInterval(() => {
        if (serverErrorActive) return; // Don't poll during server error
        pollCount++;
        // Back off: skip probabilistically on errors (50%, 75%, 87.5%...)
        if (consecutiveErrors > 0) {
          const skipChance = 1 - 1 / Math.pow(2, Math.min(consecutiveErrors, 4));
          if (Math.random() < skipChance) return;
        }
        fetchUnreadCount().then(() => { consecutiveErrors = 0; }).catch(() => { consecutiveErrors++; });
        // Full notification fetch only every 4th cycle (~6.5min), only if WS disconnected
        if (!wsConnectedRef.current && pollCount % 4 === 0) {
          setTimeout(() => fetchNotifications(false), 5000);
        }
      }, BASE_POLL_INTERVAL);
      
      // Debounced visibility handler — throttled to prevent burst
      let visibilityDebounce = null;
      let lastVisibilityFetch = 0;
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          const now = Date.now();
          if (now - lastVisibilityFetch < 30000) return; // Skip if fetched within 30s
          clearTimeout(visibilityDebounce);
          visibilityDebounce = setTimeout(() => {
            lastVisibilityFetch = Date.now();
            fetchNotifications(false);
            fetchUnreadCount().catch(() => {});
          }, 3500);
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        clearInterval(interval);
        clearTimeout(visibilityDebounce);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        if (wsRef.current) {
          wsRef.current.onclose = null; // prevent reconnect on intentional close
          wsRef.current.close();
        }
      };
    }
  }, [user]);

  const connectWebSocket = () => {
    if (!user) return;
    // Close existing connection before creating new one
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }
    
    // Track reconnection attempts for exponential backoff
    if (!connectWebSocket._attempts) connectWebSocket._attempts = 0;
    if (!connectWebSocket._fastCloses) connectWebSocket._fastCloses = 0;
    
    // Circuit breaker: if WS keeps dying instantly, stop trying
    if (connectWebSocket._fastCloses >= 3) {
      wsConnectedRef.current = false;
      return; // Polling fallback handles notifications
    }
    
    // Max 5 reconnection attempts, then rely on polling fallback
    if (connectWebSocket._attempts >= 5) {
      wsConnectedRef.current = false;
      return; // Polling fallback (line ~99) handles notifications
    }
    
    const openedAt = Date.now();
    
    try {
      const socket = new WebSocket(`${WS_URL}/api/ws/notifications/${user.id}`);
      
      socket.onopen = () => {
        wsConnectedRef.current = true;
        connectWebSocket._attempts = 0; // Reset on successful connection
        connectWebSocket._fastCloses = 0;
      };
      
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === "notification") {
            const notif = data.notification;
            setNotifications(prev => [notif, ...prev]);
            setUnreadCount(prev => prev + 1);
            playNotificationSound();
            
            // Use imported showBrowserNotification (not local)
            showBrowserNotification(notif.title, {
              body: notif.message,
              tag: `taskeri-${notif.type}-${notif.id}`
            });
            
            // Rich toast with title + message
            toast.info(notif.message ? `${notif.title}: ${notif.message}` : notif.title);
          }
        } catch (error) {
          console.error("Error processing WebSocket message:", error);
        }
      };
      
      socket.onerror = () => {
        wsConnectedRef.current = false;
      };
      
      socket.onclose = () => {
        wsConnectedRef.current = false;
        
        // Circuit breaker: detect instant closes
        const duration = Date.now() - openedAt;
        if (duration < 3000) {
          connectWebSocket._fastCloses++;
        }
        
        if (connectWebSocket._fastCloses >= 3) return;
        
        connectWebSocket._attempts++;
        // Exponential backoff: 5s, 10s, 20s, 40s, 80s
        const delay = Math.min(5000 * Math.pow(2, connectWebSocket._attempts - 1), 80000);
        reconnectTimerRef.current = setTimeout(() => {
          if (user) connectWebSocket();
        }, delay);
      };
      
      wsRef.current = socket;
    } catch {
      // WebSocket constructor can throw if URL is invalid
      wsConnectedRef.current = false;
    }
  };

  const fetchNotifications = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await dedupedGet(`${API}/notifications`, {
        withCredentials: true
      });
      const data = response.data;
      // Handle both paginated {notifications: [...]} and legacy array formats
      const notifArray = Array.isArray(data) ? data : (data.notifications || []);
      const sorted = notifArray.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA;
      });
      setNotifications(sorted);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements/active`, { withCredentials: true });
      setActiveAnnouncements(res.data.announcements || []);
    } catch (e) {}
  };


  const fetchUnreadCount = async () => {
    try {
      const response = await dedupedGet(`${API}/notifications/unread-count`, {
        withCredentials: true,
        timeout: 8000
      });
      const newCount = response.data.count;
      
      // Only update count, don't play sound here
      // Sound is played via WebSocket when new notifications arrive
      setUnreadCount(newCount);
    } catch (error) {
      // Re-throw so caller's backoff tracking can detect failures
      throw error;
    }
  };

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) return false;
    if (Notification.permission === "default") {
      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          toast.success("Browser notifications enabled!");
          showBrowserNotification("Notifications Enabled", {
            body: "You'll now receive alerts for new messages, job updates, and more.",
            tag: "test-notification"
          });
          return true;
        } else if (permission === "denied") {
          console.log("❌ Notification permission denied");
          toast.error("Notifications blocked. Enable them in browser settings to receive alerts.");
          return false;
        }
      } catch (error) {
        console.error("Error requesting notification permission:", error);
        return false;
      }
    } else if (Notification.permission === "granted") {
      return true;
    }
    return false;
  };

  useEffect(() => {
    // Request permission after user interacts with the page
    const requestOnInteraction = () => {
      requestNotificationPermission();
      // Remove listener after first interaction
      document.removeEventListener('click', requestOnInteraction);
    };
    
    // Wait for user interaction before requesting permission
    document.addEventListener('click', requestOnInteraction);
    
    return () => {
      document.removeEventListener('click', requestOnInteraction);
    };
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      // If already read locally, skip the server call & local decrement entirely.
      const target = notifications.find(n => n.id === notificationId);
      if (target?.is_read) return;

      const resp = await axios.patch(`${API}/notifications/${notificationId}/read`, {}, {
        withCredentials: true
      });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      // Server tells us whether the row actually changed (idempotent calls
      // return changed:false). Only decrement on real changes.
      if (resp?.data?.changed !== false) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        withCredentials: true
      });
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation(); // Prevent triggering the click handler
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        withCredentials: true
      });
      setNotifications(notifications.filter(n => n.id !== notificationId));
      // Update unread count if the deleted notification was unread
      const deletedNotif = notifications.find(n => n.id === notificationId);
      if (deletedNotif && !deletedNotif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error("Failed to delete notification");
    }
  };

  const deleteAllNotifications = async () => {
    try {
      await axios.delete(`${API}/notifications`, {
        withCredentials: true
      });
      setNotifications([]);
      setUnreadCount(0);
      toast.success("All notifications deleted");
    } catch (error) {
      console.error("Error deleting all notifications:", error);
      toast.error("Failed to delete notifications");
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    const target = getNotificationRoute(notification.type, notification.data || {});
    navigate(target);
    setIsOpen(false);
  };

  const getIcon = (type) => {
    switch (type) {
      // Job notifications
      case "new_job": return <Briefcase size={20} className="notification-icon-job" />;
      case "job_alert": return <Zap size={20} className="notification-icon-job" />;
      case "new_bid": return <DollarSign size={20} className="notification-icon-bid" />;
      case "bid_accepted": return <CheckCircle size={20} className="notification-icon-success" />;
      case "job_started": return <MapPin size={20} className="notification-icon-tracking" />;
      case "hero_on_way": return <MapPin size={20} className="notification-icon-tracking" />;
      case "job_completed": return <Check size={20} className="notification-icon-complete" />;
      case "pending_confirmation": return <AlertCircle size={20} className="notification-icon-warning" />;
      
      // Payment notifications
      case "payment_received": 
      case "payment_successful": 
      case "payment_confirmed": return <DollarSign size={20} className="notification-icon-success" />;
      
      // Money transfer notifications
      case "refund_request": return <RefreshCw size={20} className="notification-icon-refund" />;
      case "refund_approved": return <CheckCircle size={20} className="notification-icon-success" />;
      case "refund_rejected": return <XCircle size={20} className="notification-icon-error" />;
      case "money_request": return <CreditCard size={20} className="notification-icon-request" />;
      case "money_request_rejected": return <XCircle size={20} className="notification-icon-error" />;
      case "money_received": 
      case "payment_sent": return <Send size={20} className="notification-icon-sent" />;
      
      // Review notifications
      case "review_received": return <Star size={20} className="notification-icon-review" />;
      
      // Verification notifications
      case "verification_approved": return <UserCheck size={20} className="notification-icon-success" />;
      case "verification_rejected": return <XCircle size={20} className="notification-icon-error" />;
      
      // No-show / suspension notifications
      case "no_show_reported":
      case "no_show_confirmed":
      case "bidding_suspended":
      case "appeal_rejected": return <XCircle size={20} className="notification-icon-error" />;
      case "appeal_approved": return <CheckCircle size={20} className="notification-icon-success" />;
      
      // Job reminder notifications
      case "job_reminder": return <Clock size={20} className="notification-icon-reminder" />;
      
      default: return <AlertCircle size={20} className="notification-icon-default" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (!user) return null;

  return (
    <>
      {/* Bell Icon */}
      <div className="notification-bell-container" ref={bellRef}>
        <button className="notification-bell" data-testid="notification-bell" onClick={() => setIsOpen(!isOpen)}>
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
          )}
        </button>
      </div>

      {/* Notification Panel */}
      {isOpen && (
        <>
          <div className="notification-overlay" onClick={() => setIsOpen(false)} />
          <div className="notification-panel" ref={panelRef}>
            {/* Header */}
            <div className="notification-header">
              <h3>Notifications</h3>
              <div className="notification-header-actions">
                {notifications.length > 0 && (
                  <button className="btn-clear-all" onClick={deleteAllNotifications} title="Clear all">
                    <Trash2 size={16} />
                  </button>
                )}
                {unreadCount > 0 && (
                  <button className="btn-mark-all-read" onClick={markAllAsRead}>
                    Mark all read
                  </button>
                )}
                <button className="btn-close-panel" onClick={() => setIsOpen(false)}>
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Browser Notification Permission Banner */}
            {!browserNotifEnabled && 'Notification' in window && Notification.permission !== 'denied' && (
              <div className="browser-notif-banner">
                <div className="banner-content">
                  <Bell size={18} />
                  <span>Enable browser notifications to get alerts outside the app</span>
                </div>
                <button 
                  className="btn-enable-notif"
                  onClick={async () => {
                    await requestNotificationPermission();
                  }}
                >
                  Enable
                </button>
              </div>
            )}

            {/* Push Notification Banner - for mobile users */}
            {pushSupported && !pushSubscribed && browserNotifEnabled && (
              <div className="push-notif-banner">
                <div className="banner-content">
                  <Smartphone size={18} />
                  <span>Get notifications even when browser is closed</span>
                </div>
                <button 
                  className="btn-enable-push"
                  onClick={handlePushToggle}
                  disabled={pushLoading}
                >
                  {pushLoading ? 'Enabling...' : 'Enable'}
                </button>
              </div>
            )}

            {/* Push Notification Enabled Indicator */}
            {pushSupported && pushSubscribed && (
              <div className="push-enabled-indicator">
                <CheckCircle size={14} />
                <span>Push notifications enabled</span>
                <button 
                  className="btn-disable-push"
                  onClick={handlePushToggle}
                  disabled={pushLoading}
                >
                  Disable
                </button>
              </div>
            )}

            {/* Active Announcements in Notification Panel */}
            {activeAnnouncements.length > 0 && (
              <div className="notification-announcements-section" data-testid="notif-announcements">
                <div style={{ padding: '8px 16px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#8448fc', letterSpacing: '0.5px', borderBottom: '1px solid #f3f4f6' }}>Announcements</div>
                {activeAnnouncements.map(a => (
                  <div key={a.id} className="notification-item announcement-item" style={{ borderLeft: `3px solid ${a.priority === 'urgent' ? '#dc2626' : a.priority === 'high' ? '#b45309' : '#8448fc'}` }}>
                    <div className="notification-icon-wrapper" style={{ color: '#8448fc' }}>
                      <Megaphone size={20} />
                    </div>
                    <div className="notification-content">
                      <div className="notification-title" style={{ color: '#8448fc' }}>{a.title}</div>
                      <div className="notification-message">{a.message}</div>
                      <div className="notification-time">{formatTime(a.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Notifications List */}
            <div className="notification-list">
              {loading ? (
                <div className="notification-loading">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="notification-empty">
                  <Bell size={48} className="empty-icon" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? "unread" : ""}`}
                    data-testid={`notification-item-${notification.type}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon-wrapper">
                      {getIcon(notification.type)}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">
                        {notification.type === 'job_reminder' && notification.data?.prep_tip ? (
                          <>
                            <span>{notification.message?.split('\n\nPrep tip:')[0]}</span>
                            <div className="notification-prep-tip" data-testid="notification-prep-tip">
                              <Clock size={12} />
                              <span>{notification.data.prep_tip}</span>
                            </div>
                          </>
                        ) : (
                          notification.message
                        )}
                      </div>
                      <div className="notification-time">{formatTime(notification.created_at)}</div>
                    </div>
                    <button 
                      className="notification-delete-btn"
                      onClick={(e) => deleteNotification(notification.id, e)}
                      title="Delete notification"
                    >
                      <Trash2 size={16} />
                    </button>
                    {!notification.is_read && <div className="notification-dot" />}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NotificationCenter;
